(() => {
  if (customElements.get('cavador-flooring-compare')) return;

  const fields = ['material', 'collection', 'model', 'colour', 'finish', 'dimensions', 'thickness', 'water_resistance', 'underpad', 'installation', 'coverage'];

  const cleanText = value => typeof value === 'string' ? value.trim().slice(0, 600) : '';

  const productUrl = value => {
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[a-z0-9-]+\/?$/i.test(url.pathname)) return '';
      return url.pathname;
    } catch {
      return '';
    }
  };

  const imageUrl = value => {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      if (url.protocol !== 'https:' || (url.hostname !== 'cdn.shopify.com' && !(url.origin === window.location.origin && url.pathname.startsWith('/cdn/shop/')))) return '';
      return url.href;
    } catch {
      return '';
    }
  };

  const sanitize = input => {
    if (!input || typeof input !== 'object' || !/^\d+$/.test(String(input.id)) || !/^[a-z0-9-]+$/.test(input.handle || '')) return null;
    const title = cleanText(input.title);
    const url = productUrl(input.url);
    if (!title || !url) return null;
    const values = {};
    fields.forEach(field => {
      if (field === 'coverage') values[field] = typeof input.values?.coverage === 'number' && Number.isFinite(input.values.coverage) && input.values.coverage > 0 ? input.values.coverage : null;
      else values[field] = cleanText(input.values?.[field]);
    });
    if (/^TF[A-Z]*\d+[A-Z0-9]*$/i.test(values.colour.replace(/[\s_-]/g, ''))) values.colour = '';
    return { id: String(input.id), handle: input.handle, title, url, image: imageUrl(input.image), values };
  };

  customElements.define('cavador-flooring-compare', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.hub = this.closest('cavador-flooring-hub');
      this.dialog = this.querySelector('[data-floor-compare-dialog]');
      if (!this.hub || !this.dialog || typeof this.dialog.showModal !== 'function') return;
      try {
        this.labels = JSON.parse(this.querySelector('[data-floor-compare-labels]').textContent);
      } catch {
        return;
      }
      this.initialized = true;
      this.abort = new AbortController();
      this.storageKey = 'cavador-flooring-compare:v1:' + (this.dataset.locale || 'en');
      this.catalog = new Map();
      this.controls = new Map();
      this.selected = new Map();
      this.tray = this.querySelector('[data-floor-compare-tray]');
      this.list = this.querySelector('[data-floor-compare-shortlist]');
      this.count = this.querySelector('[data-floor-compare-count]');
      this.hint = this.querySelector('[data-floor-compare-hint]');
      this.openButton = this.querySelector('[data-floor-compare-open]');
      this.clearButton = this.querySelector('[data-floor-compare-clear]');
      this.closeButton = this.querySelector('[data-floor-compare-close]');
      this.tableHost = this.querySelector('[data-floor-compare-table]');
      this.status = this.querySelector('[data-floor-compare-status]');
      this.numberFormat = new Intl.NumberFormat(this.dataset.locale || 'en', { maximumFractionDigits: 2 });
      this.readCatalog();
      this.restore();
      this.bind();
      this.render();
    }

    disconnectedCallback() {
      this.abort?.abort();
      if (this.dialog?.open) this.dialog.close();
      document.body.classList.remove('cfh-compare-is-open');
      clearTimeout(this.statusTimer);
      this.initialized = false;
    }

    readCatalog() {
      this.hub.querySelectorAll('[data-floor-compare-product]').forEach(script => {
        let record;
        try { record = sanitize(JSON.parse(script.textContent)); } catch { return; }
        if (!record) return;
        const card = script.closest('[data-card]');
        const template = card.querySelector('[data-floor-compare-facts]');
        template?.content.querySelectorAll('dl > div').forEach(row => {
          const label = row.querySelector('dt')?.textContent.trim();
          const value = cleanText(row.querySelector('dd')?.textContent);
          ['water_resistance', 'underpad'].forEach(field => {
            if (label === this.labels.fields[field]) record.values[field] = value;
          });
        });
        this.catalog.set(record.id, record);
        const control = card.querySelector('[data-floor-compare-toggle]');
        if (!control) return;
        this.controls.set(record.id, control);
        control.closest('[data-floor-compare-pick]').hidden = false;
      });
    }

    restore() {
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
        if (saved?.version !== 1 || !Array.isArray(saved.selected)) return;
        saved.selected.slice(0, 3).forEach(item => {
          const record = sanitize(item);
          if (record) this.selected.set(record.id, this.catalog.get(record.id) || record);
        });
      } catch {}
    }

    save() {
      try {
        if (this.selected.size) sessionStorage.setItem(this.storageKey, JSON.stringify({ version: 1, selected: [...this.selected.values()] }));
        else sessionStorage.removeItem(this.storageKey);
      } catch {}
    }

    bind() {
      const options = { signal: this.abort.signal };
      this.hub.addEventListener('change', event => {
        const control = event.target.closest('[data-floor-compare-toggle]');
        if (!control) return;
        const record = this.catalog.get(control.value);
        if (!record) return;
        if (!control.checked) {
          this.remove(record.id);
          return;
        }
        if (this.selected.size >= 3 && !this.selected.has(record.id)) {
          control.checked = false;
          this.hint.textContent = this.labels.limit;
          this.announce(this.labels.limit);
          return;
        }
        this.selected.set(record.id, record);
        this.render();
        this.announce(this.labels.added.replace('__TITLE__', record.title));
      }, options);
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-floor-compare-remove]');
        if (button) this.remove(button.dataset.floorCompareRemove);
      }, options);
      this.openButton.addEventListener('click', () => this.open(), options);
      this.clearButton.addEventListener('click', () => {
        this.selected.clear();
        this.render();
        this.close();
        this.announce(this.labels.clear_status);
        this.focusVisible();
      }, options);
      this.closeButton.addEventListener('click', () => this.close(), options);
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.close();
      }, options);
      this.dialog.addEventListener('close', () => document.body.classList.remove('cfh-compare-is-open'), options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.close();
      }, options);
      window.addEventListener('pageshow', () => {
        this.close();
        this.selected.clear();
        this.restore();
        this.render();
      }, options);
    }

    announce(message) {
      clearTimeout(this.statusTimer);
      this.status.textContent = '';
      this.statusTimer = setTimeout(() => { this.status.textContent = message; }, 20);
    }

    isVisibleControl(element) {
      if (!element?.isConnected || element.disabled || !element.matches('button, a[href], input, select, textarea, summary, [tabindex]')) return false;
      for (let ancestor = element; ancestor; ancestor = ancestor.parentElement) {
        if (ancestor.hidden || ancestor.hasAttribute('inert') || ancestor.matches('dialog:not([open])')) return false;
        if (ancestor.matches('details:not([open])') && !ancestor.querySelector('summary')?.contains(element)) return false;
        const style = getComputedStyle(ancestor);
        if (style.display === 'none' || style.visibility === 'hidden') return false;
      }
      return true;
    }

    focusVisible(preferred = []) {
      const candidates = [...preferred, ...this.controls.values(), this.hub.querySelector('[data-search]'), this.hub.querySelector('[data-filter-panel] > summary')];
      candidates.find(element => this.isVisibleControl(element))?.focus({ preventScroll: true });
    }

    text(tag, className, value) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      element.textContent = value;
      return element;
    }

    image(record, className) {
      if (!record.image) return this.text('span', className === 'cfh-compare__thumb' ? className : 'cfh-compare__finish-placeholder', className === 'cfh-compare__thumb' ? '' : this.labels.no_image);
      const image = document.createElement('img');
      image.className = className;
      image.src = record.image;
      image.alt = className === 'cfh-compare__thumb' ? '' : record.title;
      image.width = className === 'cfh-compare__thumb' ? 42 : 640;
      image.height = className === 'cfh-compare__thumb' ? 42 : 480;
      image.decoding = 'async';
      return image;
    }

    removeButton(record) {
      const button = this.text('button', 'cfh-compare__remove', '×');
      button.type = 'button';
      button.dataset.floorCompareRemove = record.id;
      button.setAttribute('aria-label', this.labels.remove.replace('__TITLE__', record.title));
      return button;
    }

    render() {
      this.save();
      this.controls.forEach((control, id) => { control.checked = this.selected.has(id); });
      this.hub.classList.toggle('has-floor-compare', this.selected.size > 0);
      this.tray.hidden = this.selected.size === 0;
      this.count.textContent = this.labels.selected.replace('__COUNT__', this.selected.size);
      this.hint.textContent = this.selected.size < 2 ? this.labels.add_one : this.labels.choose;
      this.openButton.disabled = this.selected.size < 2;
      const fragment = document.createDocumentFragment();
      this.selected.forEach(record => {
        const item = document.createElement('li');
        const copy = document.createElement('div');
        copy.className = 'cfh-compare__short-name';
        copy.append(this.text('span', '', record.title), this.text('span', '', record.values.model || record.title));
        item.append(this.image(record, 'cfh-compare__thumb'), copy, this.removeButton(record));
        fragment.append(item);
      });
      this.list.replaceChildren(fragment);
    }

    remove(id) {
      const record = this.selected.get(id);
      if (!record) return;
      const index = [...this.selected.keys()].indexOf(id);
      const inDialog = this.dialog.open;
      const focusedRemove = document.activeElement?.dataset.floorCompareRemove === id;
      this.selected.delete(id);
      this.render();
      if (inDialog) {
        if (this.selected.size < 2) this.close();
        else {
          this.renderTable();
          const buttons = [...this.tableHost.querySelectorAll('[data-floor-compare-remove]')];
          buttons[Math.min(index, buttons.length - 1)]?.focus({ preventScroll: true });
        }
      } else if (focusedRemove) {
        this.focusVisible([this.list.querySelector('[data-floor-compare-remove]'), this.controls.get(id)]);
      }
      this.announce(this.labels.removed.replace('__TITLE__', record.title));
    }

    renderTable() {
      const records = [...this.selected.values()];
      const table = document.createElement('table');
      table.className = 'cfh-compare__table';
      table.dataset.floorCount = records.length;
      const head = document.createElement('thead');
      const headRow = document.createElement('tr');
      const corner = this.text('th', '', this.labels.details);
      corner.scope = 'col';
      headRow.append(corner);
      records.forEach(record => {
        const cell = document.createElement('th');
        cell.scope = 'col';
        const finish = document.createElement('div');
        finish.className = 'cfh-compare__finish';
        finish.append(this.image(record, 'cfh-compare__finish-image'), this.removeButton(record));
        cell.append(finish, this.text('span', 'cfh-compare__finish-title', record.title), this.text('span', 'cfh-compare__finish-model', record.values.model || ''));
        const link = this.text('a', 'cfh-compare__view', this.labels.view);
        link.href = record.url;
        link.setAttribute('aria-label', this.labels.view + ': ' + record.title);
        cell.append(link);
        headRow.append(cell);
      });
      head.append(headRow);
      const body = document.createElement('tbody');
      fields.forEach(field => {
        const row = document.createElement('tr');
        const label = this.text('th', '', this.labels.fields[field]);
        label.scope = 'row';
        row.append(label);
        records.forEach(record => {
          const value = field === 'coverage' && record.values.coverage ? this.labels.sqft.replace('__AMOUNT__', this.numberFormat.format(record.values.coverage)) : field === 'coverage' ? '' : record.values[field];
          row.append(this.text('td', value ? '' : 'cfh-compare__missing', value || this.labels.missing));
        });
        body.append(row);
      });
      table.append(head, body);
      this.tableHost.replaceChildren(table);
    }

    open() {
      if (this.selected.size < 2 || this.dialog.open) return;
      this.returnFocus = document.activeElement;
      this.renderTable();
      this.tableHost.scrollLeft = 0;
      this.dialog.showModal();
      document.body.classList.add('cfh-compare-is-open');
      this.closeButton.focus({ preventScroll: true });
    }

    close() {
      if (!this.dialog.open) return;
      this.dialog.close();
      document.body.classList.remove('cfh-compare-is-open');
      this.focusVisible([this.returnFocus, this.clearButton]);
    }
  });
})();
